import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useObjectiveMetadata } from './useObjectiveMetadata';

const STATUS_LABELS = {
  not_started: 'Non commencé',
  in_progress: 'En cours',
  blocked: 'Bloqué',
  done: 'Terminé',
};

const STATUS_COLORS = {
  not_started: 'bg-slate-200 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  blocked: 'bg-rose-100 text-rose-700',
  done: 'bg-emerald-100 text-emerald-700',
};

const ObjectiveTable = ({ objectives = [] }) => {
  const navigate = useNavigate();
  const { getProjectName, getOwnerName, getSprintName } = useObjectiveMetadata(objectives);

  if (!objectives.length) {
    return (
      <div className="rounded-xl border border-indigo-100 bg-white px-4 py-3 text-sm text-slate-500">
        Aucun objectif à afficher.
      </div>
    );
  }

  const handleObjectiveClick = (objective) => {
    navigate(`/objectives?project=${objective.project_id}&objective=${objective.id}`);
  };

  return (
    <div className="space-y-3">
      {objectives.map((objective) => {
        const statusInfo = STATUS_COLORS[objective.status] || 'bg-slate-200 text-slate-700';
        const projectLabel = getProjectName(objective);
        const ownerLabel = getOwnerName(objective);
        const sprintLabel = getSprintName(objective);

        return (
          <button
            key={objective.id}
            type="button"
            onClick={() => handleObjectiveClick(objective)}
            className="w-full rounded-2xl border border-indigo-100/80 bg-white/90 p-4 text-left shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-900">{objective.name}</p>
                <p className="text-xs text-slate-500">{projectLabel}</p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo}`}
              >
                {STATUS_LABELS[objective.status] || objective.status}
              </span>
            </div>

            <div className="mt-3 grid gap-4 text-xs text-slate-600 sm:grid-cols-3">
              <div className="flex flex-col">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">Owner</span>
                <span className="text-sm text-slate-900">{ownerLabel}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">Effort</span>
                <span className="inline-flex w-fit items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
                  {objective.effort ?? '—'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">Sprint</span>
                <span className="text-sm text-slate-900">{sprintLabel}</span>
              </div>
            </div>

            {(objective.description || objective.comment) && (
              <div className="mt-3 rounded-xl bg-indigo-50/80 p-3 text-xs text-indigo-900">
                {objective.description && (
                  <p className="mb-1">
                    <span className="font-semibold">Description :</span> {objective.description}
                  </p>
                )}
                {objective.comment && (
                  <p>
                    <span className="font-semibold">Commentaire :</span> {objective.comment}
                  </p>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

ObjectiveTable.propTypes = {
  objectives: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      effort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      project_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      project_name: PropTypes.string,
      project: PropTypes.object,
      owner_name: PropTypes.string,
      owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      owner: PropTypes.object,
      description: PropTypes.string,
      comment: PropTypes.string,
      sprint_name: PropTypes.string,
      sprint_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sprint: PropTypes.object,
    })
  ),
};

export default ObjectiveTable;
